import { useEffect, useMemo, useState, useCallback } from "react";
import { DrawerHeader } from "../../../../components/drawer/drawer-header";
import { Drawer } from "../../../../components/drawer/drawer";
import { Form } from "antd";
import {
  FormHeaderTextarea,
  FormFieldName
} from "../../../../components/form/form";
import { Editor } from "../../../../components/editor/editor";
import ApiLegalDocuments from "../../../../api/legal-document-management";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import ApiUserUploads from "../../../../api/user-uploads";
import debounce from "../../../../common/utils/debounce";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import DiscardModal from "../../../../components/modals/discard/discard-modal.js";
import ChangesDiffModalComponent from "../../../../components/modals/change-logs/changes-diff-modal.js";
import * as mammoth from "mammoth";
import TurndownService from "turndown";

export const LegalDocumentModal = ({
  form,
  data,
  onClose,
  onSave,
  tags,
  categories,
  legalDocumentId,
  updateHasImage,
  updateIsUpdatedImage,
  isLoading,
  authors = [],
  mode,
  onDiscard,
  onPublish,
  onSaveSilent,
  remove,
  toggleActive
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editorContent, setEditorContent] = useState();
  const [articleImage, setArticleImage] = useState();
  const [isExternal, setIsExternal] = useState(data?.external ?? false);
  const navigate = useProjectNavigate();
  // const currentUser = useSelector(selectUser);
  const urlPart = "/administration/legal-documents"
  const [isChanged, setChanged] = useState(false);
  const [isDiscardModalOpen, setDiscardModalOpen] = useState(false);
  const openDiscardModal = () => setDiscardModalOpen(true);
  const closeDiscardModal = () => setDiscardModalOpen(false);
  const [isOpenChangeLogsModal, setOpenChangeLogsModal] = useState();

  const [diffLoading, setDiffLoading] = useState(false);
  const [activeVersion, setActiveVersion] = useState(null);
  const [pendingVersion, setPendingVersion] = useState(null);

  const diffFields = useMemo(() => ([
    { key: "text",       label: "",              type: "text" },
  ]), []);

  const handleImportDocx = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const imageCache = new Map();

      const result = await mammoth.convertToHtml(
        { arrayBuffer: buffer },
        {
          convertImage: mammoth.images.inline(async (image) => {
            const arrBuf = await image.read();
            const base64 = Buffer.from(arrBuf).toString("base64");
            const mimeType = image.contentType || "image/png";

            const key = base64.slice(0, 64);
            let imageId;

            if (imageCache.has(key)) {
              imageId = imageCache.get(key);
            } else {
              const resp = await ApiUserUploads.uploadLegalDocImage({
                legalDocumentId,
                base64,
                mimeType,
              });
              imageId = resp?.data?.userUpload?.id;
              imageCache.set(key, imageId);
            }

            return {
              src: "placeholder",
              alt: `image-${imageId}`,
            };
          }),
        }
      );

      const html = result.value || "";

      const turndown = new TurndownService({
        headingStyle: "atx",
        hr: "---",
        bulletListMarker: "-",
        codeBlockStyle: "fenced",
      });

      turndown.addRule("images", {
        filter: "img",
        replacement: (content, node) => {
          const alt = node.getAttribute("alt") || "";
          const src = node.getAttribute("src") || "";
          return `![${alt}](${src})`;
        },
      });

      const markdown = turndown.turndown(html);

      handleFieldChange("text", markdown);
      setEditorContent(markdown);
      setEditMode(false);

    } catch (err) {
      console.error("❌ DOCX import failed:", err);
    }
  };

  useEffect(() => {
    setPendingVersion(data || null);
  }, [data]);

  useEffect(() => {
    if (data?.status === "pending") {
      setChanged(true);
    }
  }, [data?.status]);

  const debouncedAutoSave = useCallback(
    debounce((fields) => {
      if (data?.id) {
        onSaveSilent(data.id, fields);
      }
    }, 500),
    [data?.id, onSaveSilent]
  );

  const handleFieldChange = (field, value) => {
    form.setFieldValue(field, value);
    const fields = form.getFieldsValue();
    debouncedAutoSave({ ...fields, [field]: value });
    setChanged(true);
  };

  useEffect(() => {
    if (mode === "edit") {
      setEditMode(true);
    }

    if (mode === "create") {
      setEditMode(true);
    }

    if (mode === "show") {
      setEditMode(false);
    }
  }, [mode]);

  const closeHandler = () => {
    navigate(urlPart);
    setEditMode(false);
    onClose();
  };

  const editHandler = () => {
    navigate(`${urlPart}/${legalDocumentId}/edit`);
    setEditMode(true);
  };

  const handleDiscard = async () => {
    try {
      await onDiscard?.(data.id);
      setEditorContent(undefined);
      setChanged(false);
      onClose();
      form?.resetFields();
    } catch (error) {
      console.error("Discard error:", error);
    }
  };

  const handlePublish = async () => {
    try {
      await onPublish?.(data.id);
      setChanged(false);
      navigate(`/administration/legal-documents/${data.id}`);
      setEditMode(false);
      onClose();
    } catch (error) {
      console.error("Publish error:", error);
    }
  };

  const cancelHandler = () => {
    if (legalDocumentId) {
      navigate(`${urlPart}/${legalDocumentId}`);
    } else {
      navigate(`${urlPart}`);
    }

    form.resetFields();
    setEditMode(false);
    if (data.status === "draft") {
      onClose();
    }
  };

  const onHandleContentUpdate = (value) => {
    setEditorContent(value);
    handleFieldChange("text", value);
  };

  // const onHandleUpdateImage = (image) => {
  //   setArticleImage(image);
  //   handleFieldChange("image", image);
  // };

  useEffect(() => {
    if (data?.status === "draft") {
      setEditMode(true);
    }

    if (data?.content) {
      setEditorContent(data.text);
    }

    if (data) {
      setIsExternal(data?.external);
    }
    if (data?.image) {
      setArticleImage(data.image);
    }
  }, [data]);

  async function fetchUpdatedImageUrls(content) {
    const imgMarkdownRegex = /!\[(image-[a-f0-9-]+)\]\((.*?)\)/gi;
    const imgHtmlRegex = /<img[^>]*?alt=["'](image-[a-f0-9-]+)["'][^>]*?src=["'](.*?)["'][^>]*?>/gi;

    const imageIds = new Set();
    let match;

    while ((match = imgMarkdownRegex.exec(content)) !== null) {
      imageIds.add(match[1].replace("image-", ""));
    }
    while ((match = imgHtmlRegex.exec(content)) !== null) {
      imageIds.add(match[1].replace("image-", ""));
    }

    if (imageIds.size === 0) return content;

    try {
      const response = await ApiUserUploads.getSignedUrls({ imageIds: [...imageIds] });
      const urlMap = response.data;
      return content
        .replace(imgMarkdownRegex, (match, alt, src) => {
          const imageId = alt.replace("image-", "");
          return urlMap[imageId] ? `![${alt}](${urlMap[imageId]})` : match;
        })
        .replace(imgHtmlRegex, (match, alt, src) => {
          const imageId = alt.replace("image-", "");
          return urlMap[imageId] ? `<img alt="${alt}" src="${urlMap[imageId]}" />` : match;
        });

    } catch (error) {
      console.error("❌ Failed to refresh image URLs:", error);
      return content;
    }
  }

  useEffect(() => {
    if (mode === "create") {
      setEditorContent("");
    }
  }, [mode]);

  useEffect(() => {
    if (data?.text) {
      fetchUpdatedImageUrls(data.text).then((updatedContent) => {
        setEditorContent(updatedContent);
      });
    }
  }, [data?.text]);

  useEffect(() => {
    if (!data) return;

    const raw = data.text ?? "";
    setEditorContent(raw);

    if (raw.trim() !== "") {
      fetchUpdatedImageUrls(raw)
        .then((updated) => setEditorContent(updated ?? raw))
        .catch(() => setEditorContent(raw));
    }
  }, [data?.id]);

  useEffect(() => {
    setIsExternal(!!data?.external);
  }, [data?.id]);

  const convertHtmlToMarkdown = (htmlContent) => {
    if (!htmlContent) return "";

    const imageRegex = /<img[^>]*src=["']([^"']+)["'][^>]*alt=["']([^"']*)["'][^>]*>|<img[^>]*alt=["']([^"']*)["'][^>]*src=["']([^"']+)["'][^>]*>/gi;

    return htmlContent.replace(imageRegex, (match, src1, alt1, alt2, src2) => {
      const src = src1 || src2 || '';
      const alt = alt1 || alt2 || '';
      return `<img src="${src}" alt="${alt}">`;
    });
  };

  // const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const openChangeLogsModal = async () => {
    const pendingSnap = buildPendingSnapshot();
    setPendingVersion(pendingSnap);

    setDiffLoading(true);
    try {
      if (data?.id) {
        const resp = await ApiLegalDocuments.getById(data.id, { status: "active" });
        const active = resp?.data?.legalDocument || null;
        setActiveVersion(active);
      } else {
        setActiveVersion(null);
      }
    } catch {
      setActiveVersion(null);
    } finally {
      setDiffLoading(false);
      setOpenChangeLogsModal(true);
    }
  };

  const handleChangeLogsCancel = () => {
    setOpenChangeLogsModal(false);
  };

  const buildPendingSnapshot = () => {
    const formVals = typeof form?.getFieldsValue === "function" ? form.getFieldsValue() : {};
    return {
      ...data,
      ...formVals,
      text: typeof editorContent === "string"
        ? editorContent
        : (formVals.text ?? data?.text ?? ""),
      image: articleImage ?? formVals.image ?? data?.image,
      external: typeof formVals.external === "boolean" ? formVals.external : isExternal,
      link: formVals.link ?? data?.link ?? "",
      tags: formVals.tags ?? data?.tags ?? [],
      categoryId: formVals.categoryId ?? data?.categoryId ?? null,
      userId: formVals.userId ?? data?.userId ?? null,
      title: formVals.title ?? data?.title ?? "",
    };
  };

  const drawerContent = useMemo(() => {
    return data ? (
      <Form form={form}>
        <DrawerHeader
          title={
            <Form.Item name={"title"} initialValue={data.title}>
              <FormHeaderTextarea
                readonly={true}
                placeholder={"Title"}
                />
            </Form.Item>
          }
          onEdit={editHandler}
          onDiscard={openDiscardModal}
          onPublish={handlePublish}
          onCancel={cancelHandler}
          onViewChanges={openChangeLogsModal}
          onClose={closeHandler}
          isEditing={editMode}
          isLoading={isLoading}
          isChanged={isChanged}
          resourceTitle={data?.title || data?.text}
          onImportDocx={handleImportDocx}
        />
        <Form.Item>
          <FormFieldName text="Status:" />
          <div>
            <UserActivityStatus userStatus={data.status === 'activeCopy' && isChanged ? 'pending' : data.status } />
          </div>
        </Form.Item>
        <Form.Item name={"text"}>
          {editMode ? (
            <Editor
              content={editorContent ?? data.text}
              readonly={!editMode}
              onChange={onHandleContentUpdate}
              legalDocumentId={legalDocumentId}
              disableVideoUpload={true}
            />
          ) : (
            <Markdown className={"markdown-editor lf-editor lf-editor__readonly"} rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm, remarkBreaks]}>
              {convertHtmlToMarkdown(editorContent || data.text)}
            </Markdown>
          )}
        </Form.Item>
      </Form>
    ) : null;
  }, [data, articleImage, editMode, editorContent, isExternal, isChanged]);

  return (
    <>
      <Drawer open={!!data} onClose={closeHandler} destroyOnHidden={true} zIndex={900}>
        {drawerContent}
      </Drawer>
      <DiscardModal
        open={isDiscardModalOpen}
        onConfirm={async () => {
          await handleDiscard();
          closeDiscardModal();
          onClose();
        }}
        onCancel={closeDiscardModal}
      />
      <ChangesDiffModalComponent
        key={`diff-${data?.id || 'new'}`}
        isModalOpen={isOpenChangeLogsModal}
        handleCancel={handleChangeLogsCancel}
        title={"Changes"}
        loading={diffLoading}
        pending={pendingVersion}
        active={activeVersion}
        fields={diffFields}
        titleFieldKey="title"
      />
    </>
  );
};
